/**
 * AvisoDaPharu — faixa no topo do ERP com os avisos que a Pharu publica
 * pelo painel (manutenção programada, novidade, pendência).
 *
 * - Quem decide o que aparece é o backend (`/api/plataforma/avisos-ativos`),
 *   com a unidade da SESSÃO: aviso de outro cliente nunca chega aqui.
 * - Cor só comunica estado: informativo/novidade em azul (info);
 *   manutenção/importante em laranja (warning). O `tom` vem pronto do backend.
 * - Fechar vale para esta pessoa, neste aviso: chave no localStorage
 *   prefixada pela unidade (tenantStorage) + usuário + id do aviso.
 * - Consulta ao abrir e a cada 5 minutos com a aba visível (nunca < 30 s);
 *   ao voltar para a aba, só consulta se a última leitura passou de 5 min.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiAlertTriangle, FiInfo, FiX } from "react-icons/fi";

import { useAuth } from "../../context/AuthContext";
import { carregarAvisosAtivos } from "../../services/painelAvisos";
import { getItemTenant, setItemTenant } from "../../utils/tenantStorage";

export const INTERVALO_AVISOS_MS = 5 * 60 * 1000;

const chaveFechado = (usuarioId, avisoId) => `pharuerp:aviso-pharu:fechado:${usuarioId || "anon"}:${avisoId}`;

const Pilha = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const Faixa = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  padding: 8px 8px 8px 14px;
  border-radius: 10px;
  border: 1px solid ${({ theme, $atencao }) => ($atencao ? theme.warningBorder : theme.infoBorder)};
  background: ${({ theme, $atencao }) => ($atencao ? theme.warningSurface : theme.infoSurface)};
  color: ${({ theme, $atencao }) => ($atencao ? theme.warningText : theme.infoText)};
  font-size: 13px;
  line-height: 1.4;

  > svg {
    flex: none;
  }
`;

const Texto = styled.span`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;

  b {
    font-weight: 600;
  }

  a {
    color: inherit;
    font-weight: 600;
    text-decoration: underline;
    margin-left: 6px;
    white-space: nowrap;
  }
`;

const Fechar = styled.button`
  flex: none;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 1px;
  }

  @media (max-width: 600px) {
    width: 44px;
    height: 44px;
  }
`;

function estaFechado(usuarioId, avisoId) {
  return getItemTenant(chaveFechado(usuarioId, avisoId)) === "1";
}

export default function AvisoDaPharu() {
  const { request, usuario } = useAuth();
  const usuarioId = usuario?.id;
  const [avisos, setAvisos] = useState([]);
  const [fechados, setFechados] = useState(() => new Set());
  const ultimaLeitura = useRef(0);
  const seq = useRef(0);

  const consultar = useCallback(async () => {
    const meu = ++seq.current;
    ultimaLeitura.current = Date.now();
    try {
      const r = await carregarAvisosAtivos(request);
      if (meu === seq.current) setAvisos(Array.isArray(r?.avisos) ? r.avisos : []);
    } catch {
      // A faixa é acessória: sem resposta, fica como estava e tenta no próximo ciclo.
    }
  }, [request]);

  useEffect(() => {
    if (!usuarioId) return undefined;
    consultar();
    const intervalo = window.setInterval(() => {
      if (document.visibilityState === "visible") consultar();
    }, INTERVALO_AVISOS_MS);
    const aoVoltar = () => {
      if (document.visibilityState === "visible" && Date.now() - ultimaLeitura.current >= INTERVALO_AVISOS_MS) consultar();
    };
    document.addEventListener("visibilitychange", aoVoltar);
    return () => {
      window.clearInterval(intervalo);
      document.removeEventListener("visibilitychange", aoVoltar);
    };
  }, [usuarioId, consultar]);

  const fechar = (id) => {
    setItemTenant(chaveFechado(usuarioId, id), "1");
    setFechados((atual) => new Set(atual).add(id));
  };

  const visiveis = avisos.filter((a) => !fechados.has(a.id) && !estaFechado(usuarioId, a.id));
  if (!visiveis.length) return null;

  return (
    <Pilha>
      {visiveis.map((a) => {
        const atencao = a.tom === "warn";
        const Icone = atencao ? FiAlertTriangle : FiInfo;
        return (
          <Faixa key={a.id} $atencao={atencao} role={atencao ? "alert" : "status"} data-testid="aviso-da-pharu">
            <Icone size={16} aria-hidden />
            <Texto>
              <b>{a.titulo}</b> {a.mensagem}
              {a.link_url && (
                <a href={a.link_url} target="_blank" rel="noopener noreferrer">
                  {a.link_rotulo || "Saiba mais"}
                </a>
              )}
            </Texto>
            <Fechar type="button" aria-label="Fechar aviso" title="Fechar aviso" onClick={() => fechar(a.id)}>
              <FiX size={16} aria-hidden />
            </Fechar>
          </Faixa>
        );
      })}
    </Pilha>
  );
}
